<template>
  <div
    class="setup-users-page page-table-layout"
    data-testid="setup-users"
  >
    <v-card
      data-testid="setup-users__main-card"
      class="all-width flex-card shadow-off"
      :loading="usersLoading"
      :disabled="usersLoading && initialLoading"
      loader-height="2"
    >
      <v-card-text class="page-table-layout__body">
        <div v-if="initialLoading" data-testid="setup-users__skeleton-loader">
          <v-row class="mb-4">
            <v-col cols="6">
              <v-skeleton-loader type="heading" class="mb-2" />
              <v-skeleton-loader type="text" />
            </v-col>
            <v-col cols="6" class="d-flex justify-end">
              <v-skeleton-loader type="button" width="140" />
            </v-col>
          </v-row>
          <v-skeleton-loader type="table-heading, table-row-divider@8" />
        </div>

        <template v-else>
          <UserPageHeader
            :loading="usersLoading"
            @refresh="handleRefresh"
          />

          <div class="setup-users-page__tabs-row">
            <FcTabSwitcher
              variant="underline"
              :tabs="mainTabOptions"
              :value="mainTab"
              aria-label="Users sections"
              data-testid="setup-users__main-tabs"
              @input="onMainTabChange"
            />
          </div>

          <template v-if="mainTab === MAIN_TABS.USERS">
            <UserToolbar
              :has-active-table-filter="hasActiveTableFilter"
              :page-details="pageDetails"
              @add-user="handleAddUser"
              @clear-filters="handleClearFilters"
            />

            <div
              v-if="usersLoading && !formattedRows.length"
              class="setup-users-page__panel-loader"
              data-testid="setup-users__loader"
            >
              <v-progress-circular
                indeterminate
                color="primary"
                size="40"
                width="3"
              />
            </div>

            <div
              v-else-if="isTrueEmpty"
              class="setup-users-page__empty"
              data-testid="setup-users__empty"
            >
              <p class="ma-0">No users found for this client.</p>
            </div>

            <div
              v-else-if="isFilterEmpty"
              class="setup-users-page__empty"
              data-testid="setup-users__filter-empty"
            >
              <p class="ma-0">No users match the current filters.</p>
              <v-btn
                text
                small
                class="text-none mt-2"
                data-testid="setup-users__empty-clear-filters"
                @click="handleClearFilters"
              >
                Clear filters
              </v-btn>
            </div>

            <template v-else>
              <div class="setup-users-page__table-wrap flex-fill-table">
                <UserTable
                  ref="userTable"
                  :rows="paginatedRows"
                  :table-data="filteredRows"
                  :filter-options-data="formattedRows"
                  :headers="tableHeaders"
                  :items-per-page="itemsPerPage"
                  :loading="usersLoading"
                  :can-edit="canEdit"
                  :page-details="pageDetails"
                  @edit="handleEditUser"
                  @filter-change="handleFilterChange"
                  @filter-active-change="onFilterActiveChange"
                />
              </div>
            </template>
          </template>

          <div
            v-else
            class="setup-users-page__roles-placeholder"
            data-testid="setup-users__roles-panel"
          >
            <p class="ma-0" data-testid="setup-users__roles-placeholder-text">
              {{ ROLES_TAB_PLACEHOLDER }}
            </p>
          </div>
        </template>
      </v-card-text>
    </v-card>

    <Pagination
      v-if="mainTab === MAIN_TABS.USERS && !initialLoading && filteredRows.length"
      :key="paginationKey"
      :list="filteredRows"
      :itemsperpage="itemsPerPage"
      data-testid="setup-users__pagination"
      @paginated-data="onPaginatedData"
    />

    <UserFormDialog
      :value="dialog.open"
      :mode="dialog.mode"
      :form="form"
      :errors="formErrors"
      :save-error="saveError"
      :loading="formLoading"
      :locations="allLocations"
      :available-users="availableUsersForAdd"
      @input="onDialogInput"
      @update:form="form = $event"
      @save="handleSaveUser"
      @cancel="handleCancelDialog"
    />
  </div>
</template>

<script>
import './User.scss';
import { mapActions, mapState } from 'vuex';
import { FcTabSwitcher, Pagination } from '@/modules/financialclose/components';
import { UserPageHeader, UserToolbar, UserTable } from './components';
import UserFormDialog from './modals/UserFormDialog/UserFormDialog.vue';
import {
  ITEMS_PER_PAGE,
  MAIN_TABS,
  PAGE_DETAILS,
  RIGHT,
  ROLES_TAB_PLACEHOLDER,
  TABLE_HEADERS,
} from './utils/userConstants';
import {
  buildFormFromRow,
  clearPersistedTableFilters,
  emptyUserForm,
  formatUserManagementSaveError,
  formatUserRows,
  validateUserForm,
} from './utils/userHelpers';
import { buildUserPayload } from './utils/userPayload';

export default {
  name: 'SetupUser',

  components: {
    FcTabSwitcher,
    Pagination,
    UserPageHeader,
    UserToolbar,
    UserTable,
    UserFormDialog,
  },

  data() {
    return {
      MAIN_TABS,
      ROLES_TAB_PLACEHOLDER,
      right: RIGHT,
      tableHeaders: TABLE_HEADERS.map((h) => ({ ...h })),
      itemsPerPage: ITEMS_PER_PAGE,
      pageDetails: { ...PAGE_DETAILS },
      mainTab: MAIN_TABS.USERS,
      formattedRows: [],
      filteredRows: [],
      paginatedRows: [],
      activeColumnFilters: {},
      hasActiveTableFilter: false,
      form: emptyUserForm(),
      formErrors: {},
      saveError: '',
      formLoading: false,
      initialLoading: true,
      paginationKey: 0,
      currentPage: 1,
      dialog: { open: false, mode: 'edit', userId: null },
    };
  },

  computed: {
    ...mapState('setup', {
      rawRows: 'userManagementRows',
      usersLoading: 'userManagementLoading',
      allLocations: 'userManagementLocations',
      entityUsers: 'userManagementEntityUsers',
    }),

    canEdit() {
      return this.$nova?.hasRight(this.right, 'edit') || this.$nova?.hasRight(this.right, 'add');
    },

    mainTabOptions() {
      return [
        { id: MAIN_TABS.USERS, label: 'Users', count: this.formattedRows.length },
      ];
    },

    isTrueEmpty() {
      return !this.usersLoading && this.formattedRows.length === 0;
    },

    isFilterEmpty() {
      return (
        !this.isTrueEmpty &&
        this.filteredRows.length === 0 &&
        (this.hasActiveTableFilter || Object.keys(this.activeColumnFilters || {}).length > 0)
      );
    },

    existingUserIds() {
      return new Set(
        (this.rawRows || [])
          .map((row) => String(row.user_id || ''))
          .filter(Boolean)
      );
    },

    availableUsersForAdd() {
      return (this.entityUsers || [])
        .filter((user) => {
          const id = String(user.id || user._id || '');
          return id && !this.existingUserIds.has(id);
        })
        .map((user) => ({
          name: `${user.name || ''} (${user.email || ''})`.trim(),
          labelName: user.name || '',
          email: user.email || '',
          value: user.id || user._id,
        }));
    },
  },

  watch: {
    rawRows: {
      deep: true,
      handler() {
        this.syncFormattedRows();
      },
    },
    allLocations: {
      deep: true,
      handler() {
        this.syncFormattedRows();
      },
    },
  },

  created() {
    this.bootstrap();
  },

  beforeDestroy() {
    this.activeColumnFilters = {};
    this.hasActiveTableFilter = false;
  },

  methods: {
    ...mapActions('setup', [
      'fetchUserManagement',
      'fetchUserManagementLocations',
      'fetchEntityUsersForAdd',
      'addUserManagement',
      'editUserManagement',
    ]),

    async bootstrap() {
      this.initialLoading = true;
      try {
        await Promise.all([
          this.fetchUserManagement().catch((err) => {
            console.error('[SetupUser]: fetchUserManagement error', err);
          }),
          this.fetchUserManagementLocations().catch((err) => {
            console.error('[SetupUser]: fetchUserManagementLocations error', err);
          }),
        ]);
        this.syncFormattedRows();
      } finally {
        this.initialLoading = false;
      }
    },

    async handleRefresh() {
      try {
        await Promise.all([
          this.fetchUserManagement().catch((err) => {
            console.error('[SetupUser]: fetchUserManagement error', err);
          }),
          this.fetchUserManagementLocations().catch((err) => {
            console.error('[SetupUser]: fetchUserManagementLocations error', err);
          }),
        ]);
        this.syncFormattedRows();
      } catch (err) {
        console.error('[SetupUser]: handleRefresh error', err);
      }
    },

    syncFormattedRows() {
      this.formattedRows = formatUserRows(this.rawRows, this.allLocations);
      this.applyFilters({ resetPage: false });
    },

    applyFilters({ resetPage = false } = {}) {
      const hasFilters = Object.keys(this.activeColumnFilters || {}).length > 0;
      if (hasFilters && this.$nova?.filterByDynamicCriteria) {
        this.filteredRows = this.$nova.filterByDynamicCriteria(
          this.formattedRows,
          this.activeColumnFilters
        );
      } else {
        this.filteredRows = this.formattedRows.slice();
      }

      const maxPage = Math.ceil(this.filteredRows.length / this.itemsPerPage) || 1;
      if (resetPage || this.currentPage > maxPage || this.currentPage < 1) {
        this.currentPage = 1;
        this.paginationKey += 1;
      }

      const start = (this.currentPage - 1) * this.itemsPerPage;
      this.paginatedRows = this.filteredRows.slice(start, start + this.itemsPerPage);
    },

    onPaginatedData(paginationInfo) {
      if (paginationInfo && typeof paginationInfo === 'object' && 'data' in paginationInfo) {
        this.paginatedRows = Array.isArray(paginationInfo.data) ? paginationInfo.data : [];
        this.currentPage = paginationInfo.page || 1;
      } else if (Array.isArray(paginationInfo)) {
        this.paginatedRows = paginationInfo;
      }
    },

    handleFilterChange(filters) {
      this.activeColumnFilters = filters || {};
      this.applyFilters({ resetPage: true });
    },

    onFilterActiveChange(isActive) {
      this.hasActiveTableFilter = !!isActive;
    },

    handleClearFilters() {
      this.activeColumnFilters = {};
      this.hasActiveTableFilter = false;
      clearPersistedTableFilters(this.$store, this.pageDetails);
      this.$refs.userTable?.clearTableFilter?.();
      this.applyFilters({ resetPage: true });
    },

    onMainTabChange(tabId) {
      this.mainTab = tabId;
    },

    /** Add user handler; toolbar button is gated by ADD_USER_ENABLED in UserToolbar. */
    async handleAddUser() {
      if (!this.$nova?.hasRight(this.right, 'add')) return;
      try {
        await this.fetchEntityUsersForAdd();
      } catch (err) {
        console.error('[SetupUser]: fetchEntityUsersForAdd error', err);
      }
      this.form = emptyUserForm();
      this.formErrors = {};
      this.saveError = '';
      this.dialog = { open: true, mode: 'add', userId: null };
    },

    handleEditUser(row) {
      if (!row?._id) return;
      this.form = buildFormFromRow(row);
      this.formErrors = {};
      this.saveError = '';
      this.dialog = { open: true, mode: 'edit', userId: row._id };
    },

    onDialogInput(open) {
      if (!open) this.handleCancelDialog();
    },

    handleCancelDialog() {
      this.dialog = { open: false, mode: 'edit', userId: null };
      this.form = emptyUserForm();
      this.formErrors = {};
      this.saveError = '';
      this.formLoading = false;
    },

    async handleSaveUser() {
      const errors = validateUserForm(this.form, { mode: this.dialog.mode });
      this.formErrors = errors;
      this.saveError = '';
      if (Object.keys(errors).length > 0) {
        this.$store.commit('sbError', 'Please fill all required fields correctly.');
        return;
      }

      const payload = buildUserPayload(this.form);
      this.formLoading = true;
      try {
        if (this.dialog.mode === 'edit') {
          const id = this.dialog.userId || this.form._id;
          await this.editUserManagement({ id, payload: { data: [payload] } });
        } else {
          await this.addUserManagement({ data: [payload] });
        }
        this.handleCancelDialog();
        this.syncFormattedRows();
      } catch (err) {
        this.saveError = formatUserManagementSaveError(err, this.allLocations);
        this.$store.commit(
          'sbError',
          this.saveError.split('\n')[0] || 'Failed to save user.'
        );
        console.error('[SetupUser]: handleSaveUser error', err);
      } finally {
        this.formLoading = false;
      }
    },

  },
};
</script>
